import React, { useMemo, useState } from 'react';
import { Affix, Anchor, Button, Flex, Input, Popconfirm, Spin, Tooltip } from 'antd';
import {
  CloseCircleOutlined,
  EditOutlined,
  InfoCircleOutlined,
  LeftOutlined,
} from '@ant-design/icons';
import useMenu from '../../../hooks/context/useMenu';
import { Client } from '../../../types/menu';
import useOrderBasket from '../../../hooks/context/useOrderBasket';
import Category from '../../../components/menu/Category';
import useHelpers from '../../../hooks/utility/useHelpers';
import collect from 'collect.js';
import classnames from 'classnames';
import { useNavigate } from 'react-router';
import { Routes } from '../../../routing/Router';
import Product from '../../../components/menu/Product';
import OrderFlowStepper from '../../../components/pages/order-flow/OrderFlowStepper';

export default function OrderItemsPage() {
  const { loadingMenu, categories, products } = useMenu();
  const { lineItems, quantity, subtotal, initItem, openLineItemModal, removeItem } =
    useOrderBasket();

  const { formatCurrency } = useHelpers();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const trimmedSearch = searchQuery.trim().toLowerCase();
  const filteredProducts = useMemo<Client.Product[]>(() => {
    if (!trimmedSearch) {
      return [];
    }

    return Object.values(products).filter(
      ({ name, description }) =>
        name.toLowerCase().includes(trimmedSearch) ||
        (description ?? '').toLowerCase().includes(trimmedSearch),
    );
  }, [trimmedSearch, products]);

  const onAddProduct = (product: Client.Product) => openLineItemModal(initItem(product));

  return (
    <>
      <Spin spinning={loadingMenu} fullscreen />
      <Flex justify="space-between">
        <Button
          type="text"
          onClick={() => navigate(`/${Routes.NewOrder.Root}`)}
          icon={<LeftOutlined />}
          className="my-auto"
        >
          Back to customer info
        </Button>
      </Flex>
      <OrderFlowStepper />
      <h1 className="mt-sm">Order items</h1>
      <div className="mt-sm grid grid-cols-4 gap-xl relative">
        <Affix rootClassName="mt-sm !z-1">
          <Input.Search
            value={searchQuery}
            onFocus={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            onChange={(e) => setSearchQuery(e.target.value)}
            suffix={
              <button
                onClick={() => setSearchQuery('')}
                className={classnames({
                  hidden: trimmedSearch.length === 0,
                })}
              >
                <CloseCircleOutlined />
              </button>
            }
            size="large"
            placeholder="Search"
          />
          <Anchor
            affix={false}
            items={categories.map((item) => ({
              key: `category-${item.id}`,
              href: `#category-${item.id}`,
              title: item.name,
            }))}
            onClick={() => setSearchQuery('')}
            className="mt-sm"
          />
        </Affix>
        <div className="col-span-2">
          {Boolean(trimmedSearch) &&
            filteredProducts.map((item) => (
              <Product data={item} onAdd={onAddProduct} key={`product-${item.id}`} />
            ))}
          {!trimmedSearch &&
            categories.map((item) => (
              <Category
                data={item}
                onAddProduct={onAddProduct}
                className="mt-lg first:mt-0"
                key={`category-${item.id}`}
              />
            ))}
        </div>
        {lineItems.length > 0 && (
          <div className="bg-gray-50 sticky top-sm h-fit p-sm rounded-md max-h-[calc(100vh-24px)] overflow-y-auto">
            <h6>
              {quantity} items: {formatCurrency(subtotal)}
            </h6>
            <table className="w-full mt-md">
              <tbody>
                <tr>
                  <td className="py-xs">Item</td>
                  <td className="py-xs text-right">Qty</td>
                  <td className="py-xs text-right">Price</td>
                </tr>
                {lineItems.map((item, index) => (
                  <tr key={`line-item-${index}`} className="border-t">
                    <td className="py-xs">
                      <button onClick={() => openLineItemModal(item, index)} className="contents">
                        <EditOutlined className="mr-xs" />
                        {item.product.name}
                      </button>
                      {(item.modifiers.length > 0 || Boolean(item.note)) && (
                        <Tooltip
                          title={
                            <>
                              {item.modifiers.length > 0 && (
                                <p className="text-xs">
                                  {collect(item.modifiers).implode('modifier.name', ', ')}
                                </p>
                              )}
                              {Boolean(item.note) && (
                                <p
                                  className={classnames('text-xs italic', {
                                    'mt-sm': item.modifiers.length > 0,
                                  })}
                                >
                                  Note: {item.note}
                                </p>
                              )}
                            </>
                          }
                          className="ml-xs"
                        >
                          <InfoCircleOutlined />
                        </Tooltip>
                      )}
                    </td>
                    <td className="text-right py-xs">{item.quantity}</td>
                    <td className="text-right py-xs">
                      {formatCurrency(item.package_price)}
                      <Popconfirm
                        title={`Remove ${item.product.name}`}
                        description="Are you sure?"
                        onConfirm={() => removeItem(index)}
                        okButtonProps={{ danger: true }}
                        okText="Yes"
                        cancelText="No"
                      >
                        <button className="ml-xs">
                          <CloseCircleOutlined />
                        </button>
                      </Popconfirm>
                    </td>
                  </tr>
                ))}
                <tr className="border-t">
                  <td className="py-xs">Subtotal</td>
                  <td className="py-xs" />
                  <td className="text-right py-xs">{formatCurrency(subtotal)}</td>
                </tr>
              </tbody>
            </table>
            <Button
              type="primary"
              disabled={lineItems.length === 0}
              onClick={() => navigate(`/${Routes.NewOrder.Root}/${Routes.NewOrder.OrderInfo}`)}
              size="large"
              className="w-full mt-md"
            >
              Proceed to order info
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
